"use client";

import { useState } from "react";

import { SourceCite } from "@/components/page/SourceCite";
import type { ArtefactMeta } from "@/lib/artefact";
import { fmtIntCA } from "@/lib/format";

import { selectView, type TrendView, type TrendViewId } from "../_lib/trend";

type Props = {
  /** Reusable: any page can pass its own precomputed view set. */
  views: TrendView[];
  title: string;
  /** Combined provenance for the figure (COMPONENTS.md: every figure ends
   *  with a SourceCite; multi-artefact figures use a merged meta). */
  meta: ArtefactMeta;
  /** Optional FR-007 structural-break annotation rendered above the chart
   *  (e.g. 2020 service-area taxonomy restructure). */
  breakNote?: string;
};

const W = 560;
const H = 260;
const PAD_L = 64;
const PAD_R = 16;
const PAD_T = 20;
const PAD_B = 40;

function fmtValue(v: number, kind: "thousands" | "dollars"): string {
  return kind === "thousands"
    ? `$${fmtIntCA(Math.round(v / 1000))}M`
    : `$${fmtIntCA(Math.round(v))}`;
}

function niceCeiling(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const norm = v / base;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * base;
}

/**
 * US2 — the 5-year expense trend with three switchable views (nominal /
 * inflation-adjusted / per-capita), FR-005. The toggle is the editorial
 * corrective: a reader who heard "spending grew X%" can move between
 * views and see how much is inflation vs population vs real growth.
 *
 * Every figure is read from the artefact's derived block (the parent
 * builds `views` via buildTrendViews); this component only renders +
 * toggles. Reusable by any sub-page with the same three-view need.
 *
 * SVG/SSR: every <title> tooltip is a single pre-built string (Next 16
 * strips multi-child <title> during SSR — feedback_svg_title_ssr); all
 * numbers formatted via fmtIntCA (toLocaleString is non-deterministic
 * across Node/Chrome ICU).
 */
export function MultiViewTrendChart({ views, title, meta, breakNote }: Props) {
  const [activeId, setActiveId] = useState<TrendViewId>(
    views[0]?.id ?? "nominal",
  );
  if (views.length === 0) return null;
  const view = selectView(views, activeId);

  const present = view.points.filter(
    (p): p is typeof p & { value: number } => p.value !== null,
  );
  const years = view.points.map((p) => p.year);
  const yMax = niceCeiling(Math.max(...present.map((p) => p.value), 1));

  const xFor = (year: number) => {
    if (years.length <= 1) return PAD_L + (W - PAD_L - PAD_R) / 2;
    const t = (year - years[0]) / (years[years.length - 1] - years[0]);
    return PAD_L + t * (W - PAD_L - PAD_R);
  };
  const yFor = (v: number) => H - PAD_B - (v / yMax) * (H - PAD_T - PAD_B);

  const polyline = present
    .map((p) => `${xFor(p.year)},${yFor(p.value)}`)
    .join(" ");
  const ticks = Array.from({ length: 5 }, (_, i) => (yMax / 4) * i);

  return (
    <section className="cs-paper py-12 cs-rule" aria-labelledby="trend-h">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <h2
          id="trend-h"
          className="cs-mono text-ink-3 text-xs uppercase tracking-wider mb-2"
        >
          {title}
        </h2>
        <p className="text-ink-2 cs-prose leading-relaxed mb-4">
          “City spending grew” is true in nominal dollars and far less
          dramatic once you account for inflation and a fast-growing
          population. Switch views — the gap between them is the point.
        </p>

        {/* A button group, not an ARIA tablist: we deliberately do NOT
            claim the tab pattern (no roving tabindex / arrow-key nav /
            tabpanel wiring). Plain toggle buttons with aria-pressed are
            honest about the interaction and fully keyboard-operable. */}
        <div
          role="group"
          aria-label="Trend view"
          className="flex flex-wrap gap-1 mb-4"
        >
          {views.map((v) => {
            const active = v.id === activeId;
            return (
              <button
                key={v.id}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveId(v.id)}
                className={`cs-mono text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                  active
                    ? "border-signal text-signal bg-bg-2"
                    : "border-rule text-ink-2 hover:text-signal hover:border-signal"
                }`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        {breakNote ? (
          <p className="cs-mono text-[12px] text-ink-3 leading-snug mb-3 border-l-2 border-ink-3 pl-3">
            {breakNote}
          </p>
        ) : null}

        <figure>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`${title} — ${view.label}`}
            className="w-full h-auto"
          >
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD_L}
                  x2={W - PAD_R}
                  y1={yFor(t)}
                  y2={yFor(t)}
                  stroke="var(--rule)"
                  strokeWidth={0.5}
                />
                <text
                  x={PAD_L - 6}
                  y={yFor(t) + 3}
                  textAnchor="end"
                  className="cs-mono"
                  fontSize="9"
                  fill="var(--ink-3)"
                >
                  {fmtValue(t, view.unitKind)}
                </text>
              </g>
            ))}
            <polyline
              points={polyline}
              fill="none"
              stroke="var(--ink)"
              strokeWidth={1.75}
              strokeLinejoin="round"
            />
            {present.map((p) => {
              const x = xFor(p.year);
              const y = yFor(p.value);
              const tip = `${p.year}: ${fmtValue(p.value, view.unitKind)}${
                p.citation ? ` · ${p.citation.label}` : ""
              }`;
              const dot = (
                <>
                  <circle cx={x} cy={y} r={3.5} fill="var(--ink)" />
                  <title>{tip}</title>
                </>
              );
              return (
                <g key={p.year}>
                  {p.citation ? (
                    <a
                      href={p.citation.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {dot}
                    </a>
                  ) : (
                    dot
                  )}
                  <text
                    x={x}
                    y={H - PAD_B + 16}
                    textAnchor="middle"
                    className="cs-mono"
                    fontSize="10"
                    fill="var(--ink-3)"
                  >
                    {p.year}
                  </text>
                  <text
                    x={x}
                    y={y - 9}
                    textAnchor="middle"
                    className="cs-mono"
                    fontSize="9"
                    fill="var(--ink-2)"
                  >
                    {fmtValue(p.value, view.unitKind)}
                  </text>
                </g>
              );
            })}
          </svg>
          <figcaption className="mt-3 flex flex-col gap-1">
            <span className="cs-mono text-[12px] uppercase tracking-wider text-ink-3">
              {view.unitLabel}
            </span>
            <a
              href={view.sourceLine.href}
              target="_blank"
              rel="noreferrer"
              className="cs-mono text-[12px] text-ink-3 cs-rule-underline hover:text-signal transition-colors"
            >
              source: {view.sourceLine.label}
            </a>
            <span className="text-xs text-ink-2 cs-prose leading-snug">
              {view.footnote}
            </span>
          </figcaption>
          <SourceCite meta={meta} />
        </figure>
      </div>
    </section>
  );
}
